import React, { useState, useEffect, useCallback } from 'react';
import { useJWTAuth } from '../auth/JWTAuthContext';
import { useToast } from '../contexts/ToastContext';
import AuthGuard from '../components/AuthGuard';

const AdminBattlefieldTokens = () => {
  const { getAuthHeaders, isAuthenticated } = useJWTAuth();
  const { success, error: showError } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [granting, setGranting] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [userStats, setUserStats] = useState({}); // userId -> { battlesJoined, shopVolume }
  const [grantingLevel10, setGrantingLevel10] = useState(false);
  const [level10DryRun, setLevel10DryRun] = useState(null); // { count, userIds }
  const [minimumLevel, setMinimumLevel] = useState(10);
  const [checkUserId, setCheckUserId] = useState('');
  const [userBattlefields, setUserBattlefields] = useState(null);
  const [checkingBattlefields, setCheckingBattlefields] = useState(false);

  const headers = useCallback(() => {
    return {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    };
  }, [getAuthHeaders]);

  const searchUsers = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`/api/entitlements/search-users?query=${encodeURIComponent(searchQuery)}`, {
        headers: headers()
      });
      const json = await res.json();
      
      if (json.success && json.data) {
        setSearchResults(json.data);
        // Load stats for all users
        await loadUserStats(json.data.map(u => u.id));
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Error searching users:', err);
      showError('Failed to search users');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, headers, showError]);

  const loadUserStats = async (userIds) => {
    if (userIds.length === 0) return;

    try {
      const statsPromises = userIds.map(userId =>
        fetch(`/api/admin/user-stats/${userId}`, { headers: headers() })
          .then(res => res.json())
          .then(json => ({ userId, stats: json.success ? json.data : null }))
          .catch(() => ({ userId, stats: null }))
      );

      const results = await Promise.all(statsPromises);
      const statsMap = {};
      results.forEach(({ userId, stats }) => {
        statsMap[userId] = stats || { battlesJoined: 0, shopVolume: 0 };
      });
      setUserStats(statsMap);
    } catch (err) {
      console.error('Error loading user stats:', err);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchUsers]);

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const grantTokens = async () => {
    if (selectedUserIds.size === 0) {
      showError('Please select at least one user');
      return;
    }

    setGranting(true);
    try {
      const userIdsArray = Array.from(selectedUserIds);
      const grantPromises = userIdsArray.map(userId =>
        fetch('/api/admin/grant-battlefield-token', {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ userId, amount: 1, reason: 'Admin grant' })
        })
          .then(res => res.json())
          .then(json => ({ userId, success: json.success, error: json.error }))
      );

      const results = await Promise.all(grantPromises);
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        success(`Successfully granted ${successful.length} battlefield token(s)`);
      }
      if (failed.length > 0) {
        showError(`Failed to grant tokens to ${failed.length} user(s)`);
      }

      // Clear selection and refresh stats
      setSelectedUserIds(new Set());
      await loadUserStats(userIdsArray);
    } catch (err) {
      console.error('Error granting tokens:', err);
      showError('Failed to grant tokens');
    } finally {
      setGranting(false);
    }
  };

  const formatNumber = (num) => {
    return Number(num || 0).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const grantTokensToLevel10Users = async (dryRun = false) => {
    if (!minimumLevel || minimumLevel < 1) {
      showError('Minimum level must be at least 1');
      return;
    }

    setGrantingLevel10(true);
    try {
      const res = await fetch('/api/admin/grant-battlefield-token-level-10', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ dryRun, minimumLevel: parseInt(minimumLevel) })
      });
      const json = await res.json();

      if (json.success) {
        if (dryRun) {
          setLevel10DryRun(json);
          success(`Dry run: Would grant tokens to ${json.count} user(s) with heroes above level ${minimumLevel}`);
        } else {
          success(`Successfully granted tokens to ${json.successCount} user(s)${json.errorCount > 0 ? ` (${json.errorCount} failed)` : ''}`);
          setLevel10DryRun(null);
        }
      } else {
        showError(json.error || 'Failed to grant tokens');
      }
    } catch (err) {
      console.error('Error granting tokens to level 10+ users:', err);
      showError('Failed to grant tokens');
    } finally {
      setGrantingLevel10(false);
    }
  };

  const checkUserBattlefields = async () => {
    if (!checkUserId || !checkUserId.trim()) {
      showError('Please enter a user ID');
      return;
    }

    const userId = parseInt(checkUserId.trim());
    if (isNaN(userId) || userId <= 0) {
      showError('Please enter a valid user ID');
      return;
    }

    setCheckingBattlefields(true);
    try {
      const res = await fetch(`/api/battlefields/user/${userId}`, {
        headers: headers()
      });
      const json = await res.json();

      if (json.success) {
        setUserBattlefields(json);
        success(`Found ${json.count || 0} battlefield(s) for user ${userId}`);
      } else {
        setUserBattlefields(null);
        showError(json.error || 'Failed to fetch battlefields');
      }
    } catch (err) {
      console.error('Error checking user battlefields:', err);
      showError('Failed to check battlefields');
      setUserBattlefields(null);
    } finally {
      setCheckingBattlefields(false);
    }
  };

  if (!isAuthenticated()) {
    return <AuthGuard />;
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Battlefield Tokens</h2>
        <p className="text-gray-400">
          Search for users and grant battlefield tokens
        </p>
      </div>

      {/* Check User Battlefields Section */}
      <div className="bg-gray-700/50 rounded-lg shadow-lg p-6 mb-6">
        <h3 className="text-xl font-bold mb-4">Check User Battlefields</h3>
        <p className="text-gray-400 mb-4">
          Enter a user ID to check what battlefields they own.
        </p>
        
        <div className="flex gap-3 mb-4">
          <input
            type="number"
            min="1"
            value={checkUserId}
            onChange={(e) => setCheckUserId(e.target.value)}
            placeholder="Enter User ID"
            className="flex-1 max-w-xs px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-yellow-500 focus:outline-none"
          />
          <button
            onClick={checkUserBattlefields}
            disabled={checkingBattlefields || !checkUserId}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition"
          >
            {checkingBattlefields ? 'Checking...' : 'Check Battlefields'}
          </button>
          {userBattlefields && (
            <button
              onClick={() => {
                setUserBattlefields(null);
                setCheckUserId('');
              }}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition"
            >
              Clear
            </button>
          )}
        </div>

        {userBattlefields && (
          <div className="mt-4">
            <div className="mb-2 text-sm text-gray-300">
              <strong>Total Battlefields:</strong> {userBattlefields.count || 0}
            </div>
            {userBattlefields.data && userBattlefields.data.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {userBattlefields.data.map((bf) => (
                  <div key={bf.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-bold text-white text-lg">
                          {bf.customName || bf.themeBattlefield?.name || 'Unnamed Battlefield'}
                        </div>
                        <div className="text-xs text-gray-400 font-mono mt-1">
                          ID: {bf.id}
                        </div>
                        {bf.ownerName && (
                          <div className="text-sm text-gray-300 mt-1">
                            Owner: {bf.ownerName}
                          </div>
                        )}
                      </div>
                      <div className="text-right text-sm text-gray-400">
                        <div>Theme ID: {bf.themeBattlefieldId}</div>
                        {bf.purchasedAt && (
                          <div className="text-xs mt-1">
                            Purchased: {new Date(bf.purchasedAt).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
                      <div>
                        <span className="text-gray-400">Token Efficiency:</span>
                        <div className="text-white font-bold">{bf.lootTeamTokenEfficiency}%</div>
                      </div>
                      <div>
                        <span className="text-gray-400">Item Efficiency:</span>
                        <div className="text-white font-bold">{bf.lootTeamItemEfficiency}%</div>
                      </div>
                      <div>
                        <span className="text-gray-400">Potion Efficiency:</span>
                        <div className="text-white font-bold">{bf.lootTeamPotionEfficiency}%</div>
                      </div>
                      <div>
                        <span className="text-gray-400">Gem Efficiency:</span>
                        <div className="text-white font-bold">{bf.lootTeamGemEfficiency}%</div>
                      </div>
                    </div>
                    {bf.themeBattlefield && (
                      <div className="mt-2 text-xs text-gray-400">
                        Theme: {bf.themeBattlefield.name} ({bf.themeBattlefield.battlefieldId})
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-4">
                No battlefields found for this user
              </div>
            )}
          </div>
        )}
      </div>

      {/* Grant to Level 10+ Users Section */}
      <div className="bg-gray-700/50 rounded-lg shadow-lg p-6 mb-6">
        <h3 className="text-xl font-bold mb-4">Grant Tokens to High-Level Heroes</h3>
        <p className="text-gray-400 mb-4">
          Grant 1 battlefield token to all users who have at least one hero above the specified level.
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Minimum Hero Level</label>
          <input
            type="number"
            min="1"
            value={minimumLevel}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value) && value >= 1) {
                setMinimumLevel(value);
                setLevel10DryRun(null); // Clear previous dry run when level changes
              }
            }}
            className="w-32 px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-yellow-500 focus:outline-none"
            placeholder="10"
          />
          <p className="text-xs text-gray-400 mt-1">Users with heroes above this level will receive tokens</p>
        </div>
        
        {level10DryRun && (
          <div className="mb-4 p-4 bg-blue-900/30 border border-blue-600 rounded-lg">
            <p className="text-blue-300 font-medium mb-2">
              Dry Run Results: {level10DryRun.count} user(s) would receive tokens
            </p>
            {level10DryRun.userIds && level10DryRun.userIds.length > 0 && (
              <details className="mt-2">
                <summary className="text-blue-400 cursor-pointer text-sm">Show user IDs ({level10DryRun.userIds.length})</summary>
                <div className="mt-2 text-xs text-gray-300 font-mono max-h-40 overflow-y-auto">
                  {level10DryRun.userIds.join(', ')}
                </div>
              </details>
            )}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => grantTokensToLevel10Users(true)}
            disabled={grantingLevel10}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition"
          >
            {grantingLevel10 ? 'Checking...' : 'Dry Run (Preview)'}
          </button>
          <button
            onClick={() => grantTokensToLevel10Users(false)}
            disabled={grantingLevel10 || !level10DryRun}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold rounded-lg transition"
          >
            {grantingLevel10 ? 'Granting...' : `Grant Tokens${level10DryRun ? ` (${level10DryRun.count} users)` : ''} (Level ${minimumLevel}+)`}
          </button>
          {level10DryRun && (
            <button
              onClick={() => setLevel10DryRun(null)}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition"
            >
              Clear Preview
            </button>
          )}
        </div>
      </div>

        <div className="bg-gray-700/50 rounded-lg shadow-lg p-6">
          <div className="flex gap-4 mb-4">
            <input
              type="text"
              placeholder="Search by username, email, or wallet address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-yellow-500 focus:outline-none"
            />
            <button
              onClick={grantTokens}
              disabled={selectedUserIds.size === 0 || granting}
              className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-bold rounded-lg transition"
            >
              {granting ? 'Granting...' : `Grant Token${selectedUserIds.size > 1 ? 's' : ''} (${selectedUserIds.size})`}
            </button>
          </div>

          {loading && (
            <div className="text-center text-gray-400 py-4">Searching...</div>
          )}

          {!loading && searchResults.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="pb-3 px-2 text-gray-300 font-semibold">Select</th>
                    <th className="pb-3 px-2 text-gray-300 font-semibold">User ID</th>
                    <th className="pb-3 px-2 text-gray-300 font-semibold">Username</th>
                    <th className="pb-3 px-2 text-gray-300 font-semibold">Email</th>
                    <th className="pb-3 px-2 text-gray-300 font-semibold">Wallet</th>
                    <th className="pb-3 px-2 text-gray-300 font-semibold">Battles Joined</th>
                    <th className="pb-3 px-2 text-gray-300 font-semibold">Shop Volume</th>
                    <th className="pb-3 px-2 text-gray-300 font-semibold">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((user) => {
                    const stats = userStats[user.id] || { battlesJoined: 0, shopVolume: 0 };
                    const isSelected = selectedUserIds.has(user.id);
                    return (
                      <tr
                        key={user.id}
                        className={`border-b border-gray-800 hover:bg-gray-800/50 cursor-pointer ${isSelected ? 'bg-yellow-500/20' : ''}`}
                        onClick={() => toggleUserSelection(user.id)}
                      >
                        <td className="py-3 px-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleUserSelection(user.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-5 h-5 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                          />
                        </td>
                        <td className="py-3 px-2 text-white">{user.id}</td>
                        <td className="py-3 px-2 text-white font-medium">{user.username || 'N/A'}</td>
                        <td className="py-3 px-2 text-gray-300">{user.email || 'N/A'}</td>
                        <td className="py-3 px-2 text-gray-300 font-mono text-sm">
                          {user.solana_wallet_address
                            ? `${user.solana_wallet_address.slice(0, 6)}...${user.solana_wallet_address.slice(-4)}`
                            : 'N/A'}
                        </td>
                        <td className="py-3 px-2 text-white">{formatNumber(stats.battlesJoined)}</td>
                        <td className="py-3 px-2 text-white">{formatCurrency(stats.shopVolume)}</td>
                        <td className="py-3 px-2 text-gray-400 text-sm">
                          {user.created_at
                            ? new Date(user.created_at).toLocaleDateString()
                            : 'N/A'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && searchQuery && searchResults.length === 0 && (
            <div className="text-center text-gray-400 py-8">No users found</div>
          )}

          {!loading && !searchQuery && (
            <div className="text-center text-gray-400 py-8">Enter a search query to find users</div>
          )}
        </div>
    </div>
  );
};

export default AdminBattlefieldTokens;

